import React from "react";

import { cn } from "../../../components/common/u1/cn";
import { Breadcrumb } from "../../../components/common/u1/Breadcrumb";
import { PageContainer } from "../../../components/common/u1/PageContainer";
import Header from "../../../components/public/Header";
import Footer from "../../../components/public/Footer";
import { PaymentPanel } from "../../../components/public/Payment/u1/PaymentPanel";
import { OrderSummaryPanel } from "../../../components/public/Payment/u1/OrderSummaryPanel";
import { usePaymentDetails } from "../../../components/public/Payment/u1/usePaymentDetails";
import { usePaymentCheckout } from "../../../components/public/Payment/u1/usePaymentCheckout";
import {
  PROFILE_WINDOW_DAYS,
  applyQuotedTax,
} from "../../../constants/webProfilePricing";
import { summaryCompanyName } from "../../../components/public/Payment/u1/paymentDetailsFields";
import "../../../components/common/u1/tokens.css";

/* u1 Buy a Web Profile — step 2, /u1/plans/paymentdetails.
 *
 * Visual uplift only. Every rule lives in usePaymentDetails and
 * usePaymentCheckout, transcribed from Payment/Plans/PaymentDetails.jsx and
 * PaymentsDetailsForm.jsx: the same TIP-530 guard, the same TIP-556 report
 * exemption, the same TIP-564 stale-flag handling, the same create_payment and
 * finalize sequence, and Stripe's own CheckoutForm reused untouched. The live
 * /plans/paymentdetails route still renders the original.
 */
export default function PaymentDetails() {
  const page = usePaymentDetails();
  const checkout = usePaymentCheckout({
    planDetails: page.planDetails,
    isReportRequest: page.isReportRequest,
  });

  /* TIP-646 — overlay Stripe's OWN tax once create_payment has quoted it. The
     summary is ex-VAT until then: the tax depends on the billing address and
     is automatic_tax's to compute, not this screen's to guess. Returns the
     summary untouched when the quote has not arrived or is incomplete. */
  const summary = applyQuotedTax(
    page.summary,
    checkout.paymentBreakdown?.extra_details
  );

  /* TIP-530 — the guard's effect is already redirecting; rendering a payable
     step for the one frame in between is exactly what it exists to prevent. */
  if (page.blocked) return null;

  /* Same resolution as the order panel beside it — which also reaches the
     WorldBox shape and the copy parked at purchase, so a LISTING is named
     rather than reading "this business". */
  const companyName = summaryCompanyName(page.company, page.requestedCompanyName);

  return (
    <div className="u1-root min-h-screen w-full bg-[var(--bg)]">
      {/* This route sits outside RootLayout, so it brings its own bar — pinned,
          as the uplifted screens are. */}
      <Header sticky wide />

      <PageContainer className="py-6">
        <Breadcrumb
          items={[
            { label: "Home", to: "/" },
            ...(page.company?.registered_company_name
              ? [{ label: page.company.registered_company_name }]
              : []),
            { label: "Buy a Web Profile" },
          ]}
        />

        <header className="mt-4 max-w-[640px]">
          <p className="m-0 text-[11.5px] font-bold uppercase tracking-[.13em] text-[var(--coral)] font-sans">
            Secure checkout
          </p>
          <h1 className="mb-0 mt-2 text-[26px] font-bold leading-[1.2] text-[var(--ink)] font-sans sm:text-[30px]">
            Buy a Web Profile
          </h1>
          <p className="mb-0 mt-2.5 text-[13.5px] leading-[1.55] text-[var(--muted)] font-sans">
            Unlock {companyName}&apos;s full profile for {PROFILE_WINDOW_DAYS} days.
          </p>
        </header>

        {/* The form and the order sit side by side from `md`. Neither column is
            pinned — the navbar is what stays in view. */}
        <div
          className={cn(
            "mt-6 grid grid-cols-1 items-start gap-6",
            "md:grid-cols-[minmax(0,1fr)_340px]"
          )}
        >
          <PaymentPanel
            checkout={checkout}
            planDetails={page.planDetails}
            summary={summary}
            copy={page.copy}
            isReportRequest={page.isReportRequest}
            onBack={page.goBack}
          />

          <OrderSummaryPanel
            company={page.company}
            summary={summary}
            fallbackName={page.requestedCompanyName}
            upsell={page.upsell}
            onSwitchToMembership={page.switchToMembership}
          />
        </div>
      </PageContainer>

      <Footer />
    </div>
  );
}
